'use client';

import { useCallback, useEffect, useState } from 'react';
import { Check, Eye, Pencil, X } from 'lucide-react';

import type {
  CareStateRequestStatusT,
  CareStateRequestT,
  CareStateRequestUrgencyT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { useCareStateCommands, useCareStateDomain } from '@/entities/care-state-domain';
import {
  PrescriptionCancelButton,
  isPrescriptionWorkflowAction,
  hasUnfinishedRequestActions,
} from '@/features/prescription-cancellation';
import { SelectTool, TextFieldTool, useAgentTweakable } from '@/features/copilot/agent-tweakable';
import { ProposedActionList, type EnrichedProposedAction } from '@/features/proposed-actions-plan';
import { formatLotusUuidDateTime } from '@/shared/lib';
import { Card, LexicalMarkdownEditor, TruncatedMarkdown } from '@/shared/ui/primitives';
import { Button } from '@/shared/ui/shadcn/button';

import { CareStateRequestRow } from './CareStateRequestRow';

const STATUS_OPTIONS = [
  'Open',
  'Pending Follow Up',
  'Resolved',
  'Cancelled',
] as const satisfies ReadonlyArray<CareStateRequestStatusT>;

const URGENCY_OPTIONS = [
  'Routine',
  'Urgent',
] as const satisfies ReadonlyArray<CareStateRequestUrgencyT>;

const META_ICON_BUTTON_CLASS =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded text-foreground hover:bg-muted/40';

type KarmaOfferRange = {
  karma_offer_min: number;
  karma_offer_max: number;
};

function isValidStatus(value: string): value is CareStateRequestStatusT {
  return (STATUS_OPTIONS as readonly string[]).includes(value);
}

function isValidUrgency(value: string): value is CareStateRequestUrgencyT {
  return (URGENCY_OPTIONS as readonly string[]).includes(value);
}

function sanitizeTitle(title: string): string {
  return title.trim().replace(/\s+/g, ' ').replaceAll('.', '_');
}

function formatKarmaOffer(karmaOffer: KarmaOfferRange) {
  if (karmaOffer.karma_offer_min === karmaOffer.karma_offer_max) {
    return `${karmaOffer.karma_offer_min} KP`;
  }
  return `${karmaOffer.karma_offer_min}-${karmaOffer.karma_offer_max} KP`;
}

function EditableNoteAgentBinding({
  fieldKey,
  value,
  onChange,
}: {
  fieldKey: string;
  value: string;
  onChange: (value: string) => void;
}) {
  useAgentTweakable(TextFieldTool, fieldKey, value, onChange);
  return null;
}

export interface RequestListCardProps {
  request: CareStateRequestT;
  actions: ReadonlyArray<EnrichedProposedAction>;
  onViewPriorAuth?: () => void;
  priorAuthPresentation?: {
    label: string;
    description: string;
    actionLabel: string;
    group: 'needs-you' | 'waiting' | 'settled';
    context?: string;
  };
}

export function RequestListCard({
  request,
  actions,
  onViewPriorAuth,
  priorAuthPresentation,
}: RequestListCardProps) {
  const commands = useCareStateCommands();
  const { patientUserId, careState } = useCareStateDomain();
  const paTargets = new Set(
    (careState?.prior_auth_requests ?? [])
      .filter((pa) => pa.link.type === 'request' && pa.link.id === request.id)
      .map((pa) => pa.prescription_record_id)
  );
  const paPrescriptionRecordId = paTargets.size === 1 ? [...paTargets][0] : undefined;
  const isPrescriptionRequest =
    request.kind === 'Prior Auth' ||
    careState?.proposed_actions.some(
      (action) =>
        action.link.type === 'request' &&
        action.link.id === request.id &&
        isPrescriptionWorkflowAction(action)
    ) ||
    (careState?.prescriptions.some(
      (rx) => rx.link.type === 'request' && rx.link.id === request.id
    ) ??
      false);

  // A locally cancelled prescription can share a request with other work.
  // Close it when that remaining work finishes; external PA status stays server-owned.
  useEffect(() => {
    if (!careState || !['Open', 'Pending Follow Up'].includes(request.status)) {
      return;
    }
    const linked = (row: { link: { type?: string; id?: string } }) =>
      row.link.type === 'request' && row.link.id === request.id;
    if (careState.prescriptions.some(linked) || careState.prior_auth_requests.some(linked)) {
      return;
    }
    const cancelledPrescription = careState.proposed_actions.some(
      (action) =>
        linked(action) &&
        action.payload.tool_name === 'draft_prescription' &&
        action.todo_status === 'Archived'
    );
    if (cancelledPrescription && !hasUnfinishedRequestActions(careState, request.id)) {
      commands.execute(
        { type: 'request.setStatus', requestId: request.id, status: 'Cancelled' },
        { actor: 'clinician' }
      );
    }
  }, [careState, commands, request.id, request.status]);

  const noteValue = request.note?.value ?? '';
  const hasNote = noteValue.trim().length > 0;
  const safeTitle = sanitizeTitle(request.title);
  const tweakSuffix = `${safeTitle}__${request.id}`;
  const reportedAt = formatLotusUuidDateTime(request.id);
  const cardTone = request.urgency === 'Urgent' ? 'urgent' : 'routine';
  const isPriorAuthRequest =
    request.kind === 'Prior Auth' || request.title.toLowerCase().startsWith('prior auth:');
  const interimPriorAuthPresentation: RequestListCardProps['priorAuthPresentation'] =
    isPriorAuthRequest &&
    priorAuthPresentation === undefined &&
    request.status === 'Pending Follow Up' &&
    actions.length === 0
      ? {
          label: 'Insurance reviewing',
          description: 'No action is needed from you.',
          actionLabel: 'View status',
          group: 'waiting' as const,
        }
      : undefined;
  const displayedPriorAuthPresentation = priorAuthPresentation ?? interimPriorAuthPresentation;
  const hasPriorAuthPresentation =
    isPriorAuthRequest && displayedPriorAuthPresentation !== undefined;

  const [draftNote, setDraftNote] = useState<string | null>(null);
  const isEditingNote = draftNote !== null;

  const handleClinicianSetStatus = useCallback(
    (requestId: string, next: CareStateRequestStatusT) => {
      commands.execute(
        { type: 'request.setStatus', requestId, status: next },
        { actor: 'clinician' }
      );
    },
    [commands]
  );

  const handleClinicianSetUrgency = useCallback(
    (requestId: string, next: CareStateRequestUrgencyT) => {
      commands.execute(
        { type: 'request.setUrgency', requestId, urgency: next },
        { actor: 'clinician' }
      );
    },
    [commands]
  );

  const handleStartEditing = useCallback(() => {
    setDraftNote(noteValue);
  }, [noteValue]);

  const handleDiscardEdits = useCallback(() => {
    setDraftNote(null);
  }, []);

  const handleSaveNote = useCallback(() => {
    if (draftNote === null) {
      return;
    }
    commands.execute(
      { type: 'request.setNote', requestId: request.id, note: draftNote },
      { actor: 'clinician' }
    );
    setDraftNote(null);
  }, [commands, draftNote, request.id]);

  const onAgentSetStatus = useCallback(
    (next: string) => {
      if (!isValidStatus(next)) {
        throw new Error(`Invalid status: ${next}. Allowed: ${STATUS_OPTIONS.join(', ')}`);
      }
      const previousStatus = request.status;
      commands.execute(
        { type: 'request.setStatus', requestId: request.id, status: next },
        { actor: 'copilot' }
      );
      return {
        requestId: request.id,
        title: request.title,
        previousStatus,
        newStatus: next,
      };
    },
    [commands, request.id, request.status, request.title]
  );

  const onAgentSetUrgency = useCallback(
    (next: string) => {
      if (!isValidUrgency(next)) {
        throw new Error(`Invalid urgency: ${next}. Allowed: ${URGENCY_OPTIONS.join(', ')}`);
      }
      const previousUrgency = request.urgency;
      commands.execute(
        { type: 'request.setUrgency', requestId: request.id, urgency: next },
        { actor: 'copilot' }
      );
      return {
        requestId: request.id,
        title: request.title,
        previousUrgency,
        newUrgency: next,
      };
    },
    [commands, request.id, request.title, request.urgency]
  );

  const onAgentSetNote = useCallback(
    (next: string) => {
      commands.execute(
        { type: 'request.setNote', requestId: request.id, note: next },
        { actor: 'copilot' }
      );
      return { requestId: request.id, title: request.title, noteLength: next.length };
    },
    [commands, request.id, request.title]
  );

  useAgentTweakable(SelectTool, `status:${tweakSuffix}`, request.status, onAgentSetStatus);
  useAgentTweakable(SelectTool, `urgency:${tweakSuffix}`, request.urgency, onAgentSetUrgency);

  const editButtonLabel = hasNote ? 'Edit note' : 'Add note';
  const karmaOfferLabel =
    request.status === 'Open' && request.karma_offer_min != null && request.karma_offer_max != null
      ? formatKarmaOffer({
          karma_offer_min: request.karma_offer_min,
          karma_offer_max: request.karma_offer_max,
        })
      : null;

  return (
    <Card tone={cardTone} aria-label={request.title}>
      <EditableNoteAgentBinding
        fieldKey={`note:${tweakSuffix}`}
        value={noteValue}
        onChange={onAgentSetNote}
      />

      <CareStateRequestRow
        requestId={request.id}
        title={request.title}
        status={request.status}
        urgency={request.urgency}
        menuMode={isPrescriptionRequest ? 'resolve-only' : 'full'}
        onSetStatus={handleClinicianSetStatus}
        onSetUrgency={handleClinicianSetUrgency}
      />

      <div className="flex items-center">
        {reportedAt !== null && (
          <span className="text-xs font-medium text-muted-foreground">Reported {reportedAt}</span>
        )}
        {karmaOfferLabel !== null && (
          <span
            aria-label={`Estimated Karma ${karmaOfferLabel}`}
            className="ml-2 inline-flex items-center rounded-full border border-amber-600/30 bg-amber-500/10 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:border-amber-400/30 dark:bg-amber-300/10 dark:text-amber-200"
          >
            {karmaOfferLabel}
          </span>
        )}
        <div className="ml-auto flex items-center gap-0.5">
          {isPriorAuthRequest ? null : isEditingNote ? (
            <>
              <button
                type="button"
                aria-label="Discard note edits"
                className={META_ICON_BUTTON_CLASS}
                onClick={handleDiscardEdits}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label="Save note"
                className={META_ICON_BUTTON_CLASS}
                onClick={handleSaveNote}
              >
                <Check className="h-4 w-4" aria-hidden="true" />
              </button>
            </>
          ) : (
            <button
              type="button"
              aria-label={editButtonLabel}
              className={META_ICON_BUTTON_CLASS}
              onClick={handleStartEditing}
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {isEditingNote && !isPriorAuthRequest ? (
        <LexicalMarkdownEditor
          value={draftNote ?? ''}
          onChange={setDraftNote}
          placeholder="Write a note…"
          toolbar="none"
          autoFocus
          className="min-h-24 rounded-none border-0 bg-transparent p-0 text-sm focus-within:ring-0 focus-within:ring-offset-0"
        />
      ) : hasNote ? (
        <TruncatedMarkdown id={`request-${request.id}-note`} content={noteValue} />
      ) : null}

      {actions.length === 0 && hasPriorAuthPresentation && displayedPriorAuthPresentation ? (
        <div className="flex w-full items-center gap-2 py-1">
          <span className="shrink-0 text-sm font-medium text-foreground">
            {displayedPriorAuthPresentation.label}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
            {displayedPriorAuthPresentation.context ?? displayedPriorAuthPresentation.description}
          </span>
          {onViewPriorAuth ? (
            <Button
              type="button"
              variant="ghost"
              size="chip"
              radius="pill"
              className="shrink-0 gap-1.5 text-foreground hover:bg-muted/40"
              onClick={onViewPriorAuth}
            >
              <Eye className="size-3" aria-hidden="true" />
              {displayedPriorAuthPresentation.actionLabel}
            </Button>
          ) : null}
        </div>
      ) : null}

      {request.kind === 'Prior Auth' && actions.length === 0 ? (
        <div className="flex justify-end">
          <PrescriptionCancelButton
            patientUserId={patientUserId}
            target={{ prescriptionRecordId: paPrescriptionRecordId ?? '' }}
            disabled={
              !paPrescriptionRecordId || actions.some((item) => item.visibility === 'running')
            }
          />
        </div>
      ) : null}
      {actions.length > 0 && <ProposedActionList items={actions} />}
    </Card>
  );
}
